import React, { useState } from 'react';
import { Edit2, Trash2, Gift, Check, X, CalendarClock, DollarSign, Star, History } from 'lucide-react'; // ✅ Icono History
import type { Reward } from '../types/reward';
import { useAuth } from '../../../context/AuthContext';
import BrandLoader from '../../../components/common/BrandLoader';

const REWARD_IMG_FALLBACK = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' rx='8' fill='%23e5e7eb'/%3E%3Cpath d='M12 18h16v10a2 2 0 01-2 2H14a2 2 0 01-2-2zM10 14h20v4H10zM20 14v16M20 14c-2-4-7-4-7-1s5 1 7 1zm0 0c2-4 7-4 7-1s-5 1-7 1z' fill='none' stroke='%239ca3af' stroke-width='1.6'/%3E%3C/svg%3E"; // via.placeholder.com murió: NULL → bucle de error → parpadeo (24-ago-2026)

interface RewardsListProps {
  rewards: Reward[];
  onEdit: (reward: Reward) => void;
  onDelete: (rewardId: string) => Promise<void>;
  onViewHistory?: (reward: Reward) => void; // ✅ CAMBIO: Ahora es opcional (?) para evitar crash
  loading: boolean;
}

const RewardsList: React.FC<RewardsListProps> = ({
  rewards,
  onEdit,
  onDelete,
  onViewHistory, // ✅ Puede ser undefined
  loading
}) => {
  const { currentUser } = useAuth();
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';

  const [sortField, setSortField] = useState<keyof Reward>('title');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleSort = (field: keyof Reward) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const ensureDate = (dateInput: string | Date): Date => {
      return typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  }

  const sortedRewards = [...rewards].sort((a, b) => {
      const aDate = ensureDate(a.updatedAt);
      const bDate = ensureDate(b.updatedAt);

      if (sortField === 'points') {
        return sortDirection === 'asc' ? a[sortField] - b[sortField] : b[sortField] - a[sortField];
      } else if (sortField === 'updatedAt') {
         return sortDirection === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
      }

      const aValue = String(a[sortField] || '').toLowerCase();
      const bValue = String(b[sortField] || '').toLowerCase();

      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });

  const filteredRewards = sortedRewards.filter(reward => {
    if (filter === 'all') return true;
    return filter === 'active' ? reward.isActive : !reward.isActive;
  });

  const handleDelete = async (rewardId: string) => {
    try {
      await onDelete(rewardId);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error al eliminar recompensa:', error);
    }
  };

  const formatDate = (dateInput: Date | string | null | undefined): string => {
      if (!dateInput) return 'N/A';
      try {
          const date = ensureDate(dateInput);
          if (isNaN(date.getTime())) return 'Fecha inválida';
          return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
      } catch (e) { return 'Error fecha'; }
  };

  return (
    <div>
      {/* Filter buttons */}
      <div className="mb-4 flex flex-wrap gap-2">
        {['all', 'active', 'inactive'].map(f => (
            <button
                key={f}
                onClick={() => setFilter(f as any)}
                className={`px-3 py-1 rounded-md text-sm font-medium capitalize ${filter === f ? (f === 'active' ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300' : f === 'inactive' ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300' : 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300') : 'text-content-3 hover:bg-canvas'}`}
            >
                {f === 'all' ? 'Todas' : f === 'active' ? 'Activas' : 'Inactivas'}
            </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-6">
          <BrandLoader size={36} />
          <p className="mt-2 text-sm text-content-3">Cargando recompensas...</p>
        </div>
      ) : filteredRewards.length === 0 ? (
        <div className="text-center py-8">
          <Gift className="h-12 w-12 text-content-3 mx-auto mb-3" />
          <p className="text-content-3">No hay recompensas en esta categoría.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-line">
              <thead className="bg-surface-2">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Recompensa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('points')}>
                    {loyaltyMode === 'cashback' ? 'Costo ($)' : 'Puntos'} {sortField === 'points' && <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('updatedAt')}>
                    Actualización
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-content-3 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-line">
                {filteredRewards.map((reward) => (
                  <tr key={reward.id} className="hover:bg-surface-2 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img className="h-10 w-10 rounded-lg object-cover" src={reward.imageUrl || REWARD_IMG_FALLBACK} alt={reward.title} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = REWARD_IMG_FALLBACK; }} />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-content">{reward.title}</div>
                          {!!reward.branchNames?.length && <div className="text-[11px] font-medium text-accent" data-testid="recompensa-solo-en">Solo en {reward.branchNames.join(', ')}</div>}
                          <div className="text-sm text-content-3 truncate max-w-xs">{reward.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {loyaltyMode === 'cashback' ? (
                            <><DollarSign className="h-4 w-4 text-green-600 dark:text-green-400 mr-1" /><span className="text-sm text-green-700 dark:text-green-300 font-medium">{reward.points.toFixed(2)}</span></>
                        ) : (
                            <><Star className="h-4 w-4 text-yellow-500 mr-1" /><span className="text-sm text-content">{Math.floor(reward.points)} pts</span></>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${reward.isActive ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300'}`}>
                        {reward.isActive ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-content-3">
                        <div className="flex items-center"><CalendarClock className="h-4 w-4 mr-1.5 text-content-3"/><span>{formatDate(reward.updatedAt)}</span></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      {deleteConfirm === reward.id ? (
                        <div className="flex items-center justify-center space-x-2">
                          <button onClick={() => handleDelete(reward.id)} className="text-red-600 dark:text-red-400 hover:text-red-900 p-1 rounded hover:bg-red-100 dark:hover:bg-red-500/20" title="Confirmar"><Check className="h-4 w-4" /></button>
                          <button onClick={() => setDeleteConfirm(null)} className="text-content-2 hover:text-content p-1 rounded hover:bg-canvas" title="Cancelar"><X className="h-4 w-4" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2">
                          {/* ✅ Botón Historial con chequeo de existencia */}
                          {onViewHistory && (
                            <button onClick={() => onViewHistory(reward)} className="text-purple-600 dark:text-purple-400 hover:text-purple-900 p-1 rounded hover:bg-purple-50 dark:hover:bg-purple-500/10" title="Ver Historial de Canjes">
                                <History className="h-4 w-4" />
                            </button>
                          )}
                          <button onClick={() => onEdit(reward)} className="text-accent hover:text-blue-900 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-500/10" title="Editar">
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => setDeleteConfirm(reward.id)} className="text-red-600 dark:text-red-400 hover:text-red-900 p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10" title="Eliminar">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {filteredRewards.map((reward) => (
              <div key={reward.id} className="border rounded-lg overflow-hidden shadow-sm">
                <div className="flex items-center p-3 bg-surface-2 border-b border-line">
                  <div className="flex-shrink-0 mr-3">
                    <img src={reward.imageUrl || REWARD_IMG_FALLBACK} alt={reward.title} className="h-10 w-10 object-cover rounded-lg" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = REWARD_IMG_FALLBACK; }} />
                  </div>
                  <div className="flex-grow min-w-0">
                    <h3 className="text-sm font-medium text-content truncate">{reward.title}</h3>
                    {!!reward.branchNames?.length && <p className="text-[11px] font-medium text-accent">Solo en {reward.branchNames.join(', ')}</p>}
                    <div className="mt-1 flex items-center">
                       {loyaltyMode === 'cashback' ? (
                           <><DollarSign className="h-3 w-3 text-green-600 dark:text-green-400 mr-1" /><span className="text-xs font-medium text-green-700 dark:text-green-300">{reward.points.toFixed(2)}</span></>
                       ) : (
                           <><Star className="h-3 w-3 text-yellow-500 mr-1" /><span className="text-xs font-medium text-content-2">{Math.floor(reward.points)} pts</span></>
                       )}
                    </div>
                  </div>
                  <div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${reward.isActive ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300'}`}>
                      {reward.isActive ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-xs text-content-3 line-clamp-2 mb-2">{reward.description || <span className="italic">Sin descripción</span>}</p>
                  <div className="text-xs text-content-3 mb-3 flex items-center">
                       <CalendarClock className="h-3 w-3 mr-1 text-content-3 flex-shrink-0"/><span>Actualizado: {formatDate(reward.updatedAt)}</span>
                  </div>
                  <div className="flex justify-end space-x-2 mt-2 pt-2 border-t border-line">
                    {/* ✅ Botón Historial Móvil con chequeo */}
                    {onViewHistory && (
                        <button onClick={() => onViewHistory(reward)} className="py-1 px-2 rounded text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-500/10 hover:bg-purple-100 dark:hover:bg-purple-500/20 flex items-center">
                            <History className="h-3 w-3 mr-1" /> Canjes
                        </button>
                    )}
                    {deleteConfirm === reward.id ? (
                         <div className="flex space-x-2">
                            <button onClick={() => handleDelete(reward.id)} className="p-1 rounded bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300"><Check className="h-4 w-4" /></button>
                            <button onClick={() => setDeleteConfirm(null)} className="p-1 rounded bg-canvas text-content-2"><X className="h-4 w-4" /></button>
                         </div>
                    ) : (
                        <>
                            <button onClick={() => onEdit(reward)} className="py-1 px-2 rounded text-xs font-medium text-accent bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 flex items-center"><Edit2 className="h-3 w-3 mr-1" /> Editar</button>
                            <button onClick={() => setDeleteConfirm(reward.id)} className="py-1 px-2 rounded text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 flex items-center"><Trash2 className="h-3 w-3 mr-1" /> Eliminar</button>
                        </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default RewardsList;